import React from 'react';

export default function CorrelationHeatmap({ matrix, attributes, title = "Correlation Matrix" }) {
  if (!matrix || !attributes || matrix.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-400">
        No correlation data available
      </div>
    );
  }

  const getColor = (value) => {
    const abs = Math.abs(value);
    if (abs > 0.7) return value > 0 ? 'bg-green-500' : 'bg-red-500';
    if (abs > 0.4) return value > 0 ? 'bg-green-400' : 'bg-red-400';
    if (abs > 0.2) return value > 0 ? 'bg-green-300' : 'bg-red-300';
    return 'bg-slate-600';
  };

  const getOpacity = (value) => {
    const abs = Math.abs(value);
    if (abs > 0.7) return 'opacity-100';
    if (abs > 0.4) return 'opacity-75';
    if (abs > 0.2) return 'opacity-50';
    return 'opacity-30';
  };

  const formatAttribute = (attr) => {
    return attr.replace(/_/g, ' ').replace(/cm|sec|kg/g, '').trim();
  };

  return (
    <div className="w-full">
      {title && (
        <h3 className="text-lg font-semibold text-slate-100 mb-4">{title}</h3>
      )}
      
      <div className="overflow-x-auto">
        <div className="inline-block min-w-full">
          <div className="grid gap-1" style={{ gridTemplateColumns: `120px repeat(${attributes.length}, 60px)` }}>
            {/* Header row */}
            <div className="h-20"></div>
            {attributes.map((attr, i) => (
              <div key={i} className="h-20 flex items-end justify-center pb-2">
                <span className="text-xs text-slate-400 transform -rotate-45 origin-bottom-left whitespace-nowrap">
                  {formatAttribute(attr)}
                </span>
              </div>
            ))}

            {/* Data rows */}
            {matrix.map((row, i) => (
              <React.Fragment key={i}>
                {/* Row label */}
                <div className="flex items-center justify-end pr-2">
                  <span className="text-xs text-slate-400 text-right">
                    {formatAttribute(attributes[i])}
                  </span>
                </div>
                
                {/* Cells */}
                {row.map((value, j) => (
                  <div
                    key={j}
                    className={`h-14 flex items-center justify-center rounded ${getColor(value)} ${getOpacity(value)} cursor-pointer hover:opacity-100 transition-opacity group relative`}
                    title={`${formatAttribute(attributes[i])} vs ${formatAttribute(attributes[j])}: ${value.toFixed(3)}`}
                  >
                    <span className="text-xs font-semibold text-white">
                      {value.toFixed(2)}
                    </span>
                    
                    {/* Tooltip */}
                    <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 hidden group-hover:block z-10">
                      <div className="bg-slate-800 border border-slate-600 rounded-lg p-2 shadow-lg whitespace-nowrap">
                        <p className="text-xs text-slate-300">
                          {formatAttribute(attributes[i])} × {formatAttribute(attributes[j])}
                        </p>
                        <p className="text-xs text-slate-100 font-semibold">
                          r = {value.toFixed(3)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </React.Fragment>
            ))}
          </div>
        </div>
      </div>

      {/* Legend */}
      <div className="mt-6 flex items-center justify-center gap-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded"></div>
          <span className="text-xs text-slate-400">Strong Negative</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-slate-600 opacity-30 rounded"></div>
          <span className="text-xs text-slate-400">Weak</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded"></div>
          <span className="text-xs text-slate-400">Strong Positive</span>
        </div>
      </div>
    </div>
  );
}
